import { useTheme } from '@mui/material';
import { LineChart, type LineChartProps } from '@mui/x-charts/LineChart';
import { FC, useEffect, useMemo, useState } from 'react';
import { numberAbbreviation } from '~/scripts/numberAbbreviation';
import type { Reports } from '~/types';

export interface HistoryDataPoint {
  date: number;
  total: number;
}

interface ReportHistoryTimelineProps {
  history: Reports.ReportHistory[];
  compact?: boolean;
  LineChartProps?: Omit<LineChartProps, 'xAxis' | 'yAxis' | 'series' | 'sx'>;
}

export const ReportHistoryTimeline: FC<ReportHistoryTimelineProps> = ({
  history,
  compact = false,
  LineChartProps,
}) => {
  const theme = useTheme();

  const formatMonthYear = (yearMonth: string): string => {
    const [year, month] = yearMonth.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  };

  const monthlyData = useMemo(() => {
    const groups = new Map<string, number[]>();
    for (const point of history) {
      const str = String(point.date);
      const key = `${str.slice(0, 4)}-${str.slice(4, 6)}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(point.total);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, totals]) => {
        const sorted = [...totals].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const median =
          sorted.length % 2 === 0
            ? Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
            : sorted[mid]!;
        return { label: formatMonthYear(key), total: median };
      });
  }, [history]);

  const xData = useMemo(
    () => monthlyData.map((point) => point.label),
    [monthlyData]
  );
  const yData = useMemo(
    () => monthlyData.map((point) => point.total),
    [monthlyData]
  );
  const yMin = yData.length ? Math.min(...yData) : 0;
  const yMax = yData.length ? Math.max(...yData) : 0;

  const medianValue = useMemo(() => {
    if (!yData.length) {
      return 0;
    }
    const sorted = [...yData].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
      : sorted[mid]!;
  }, [yData]);

  const [displayData, setDisplayData] = useState<(number | null)[]>(
    yData.map(() => medianValue)
  );

  useEffect(() => {
    if (!yData.length) {
      return;
    }
    const transitionDuration = 1500;
    const interval = 30;
    const startTime = Date.now();

    const ticker = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / transitionDuration, 1);
      const eased = progress < 1 ? 1 - Math.pow(1 - progress, 3) : 1;

      if (progress >= 1) {
        clearInterval(ticker);
        setDisplayData(yData);
        return;
      }

      setDisplayData(
        yData.map((real) =>
          Math.round(medianValue + (real - medianValue) * eased)
        )
      );
    }, interval);

    return () => clearInterval(ticker);
  }, [yData, medianValue]);

  return (
    <LineChart
      xAxis={[
        {
          data: xData,
          scaleType: 'point',
          tickInterval: (_value: string, index: number) => index % 2 === 0,
          tickLabelStyle: compact ? { display: 'none' } : undefined,
          disableLine: compact,
          disableTicks: compact,
        },
      ]}
      yAxis={[
        {
          min: yMin,
          max: yMax,
          valueFormatter: (value: number) => numberAbbreviation(value, 1),
          tickLabelStyle: compact ? { display: 'none' } : undefined,
          disableLine: compact,
          disableTicks: compact,
        },
      ]}
      series={[
        {
          data: displayData,
          color: theme.palette.primary.main,
          showMark: false,
          area: true,
        },
      ]}
      sx={{
        width: '100%',
        '& .MuiAreaElement-root': { opacity: 0.15 },
      }}
      {...LineChartProps}
    />
  );
};
